import { Avatar, Button, TextArea } from "@flpstudio/design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { clsx } from "clsx/lite";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { z as validator } from "zod";

import { useAuth } from "@/hooks/use-auth";
import {
  useAddCommentMutation,
  useEditCommentMutation,
} from "@/hooks/use-comment";
import type { SDocumentType } from "types";

const MAX_LENGTH = 500;
const MIN_LENGTH = 10;
const schema = validator.object({
  comment: validator
    .string()
    .trim()
    .min(1, "Please enter a comment")
    .min(MIN_LENGTH, `Comment should be more than ${MIN_LENGTH} characters`)
    .max(MAX_LENGTH, `Comment should be ${MAX_LENGTH} characters or less`),
});

type FormSchema = validator.infer<typeof schema>;

type Props =
  | {
      commentId: string;
      commentGroupId: string;
      defaultValue: string;
      onPublish?: () => void;
      onCancel?: () => void;
    }
  | {
      commentGroupId: string;
      documentType: SDocumentType;
      onPublish?: () => void;
    };

export function CommentForm(props: Props) {
  const isEditing = "commentId" in props;
  const { user } = useAuth();
  const [showSendButton, setShowSendButton] = useState(false);

  const { mutate: editComment, isPending: isEditPending } =
    useEditCommentMutation({
      commentId: isEditing ? props.commentId : "",
      groupId: props.commentGroupId,
    });

  const { mutate: addComment, isPending: isAddPending } = useAddCommentMutation(
    {
      type: !isEditing ? props.documentType : ("" as SDocumentType),
      groupId: props.commentGroupId,
    },
  );

  const action = isEditing ? editComment : addComment;

  const isPending = isEditPending || isAddPending;

  const { formState, register, handleSubmit, getValues, reset } =
    useForm<FormSchema>({
      defaultValues: {
        comment: isEditing ? props.defaultValue : "",
      },
      reValidateMode: "onBlur",
      resolver: zodResolver(schema),
    });

  const submitHandler: SubmitHandler<FormSchema> = () => {
    const comment = getValues("comment");
    action(
      { comment },
      {
        onSuccess: () => {
          props.onPublish?.();
          if (isEditing) {
            props.onCancel?.();
          } else {
            reset();
          }
        },
      },
    );
  };

  return (
    <div className={clsx(!isEditing && "flex items-start gap-2")}>
      {!isEditing && (
        <Avatar src={user?.profileImage} alt="Profile" className="mt-0.5" />
      )}
      <form
        onSubmit={handleSubmit(submitHandler)}
        className="flex flex-1 flex-col gap-2"
      >
        <TextArea
          aria-label="Comment"
          placeholder="Post your IT questions or comments"
          minRows={1}
          autosize
          disabled={isPending}
          autoFocus={isEditing}
          onFocus={() => {
            setShowSendButton(true);
          }}
          error={formState.errors.comment?.message}
          {...register("comment", {
            onBlur: (e) => {
              if (!e.target.value.trim()) {
                setShowSendButton(false);
              }
            },
          })}
        />
        {isEditing ? (
          <div className="flex flex-row-reverse gap-1">
            <Button
              type="submit"
              loading={isPending}
              disabled={isPending}
              className="self-end lg:max-w-fit"
            >
              Save
            </Button>
            <Button onClick={props.onCancel} variant="white">
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            type="submit"
            loading={isPending}
            disabled={isPending}
            className={clsx(
              "self-end lg:max-w-fit",
              !showSendButton && "hidden",
            )}
          >
            Send
          </Button>
        )}
      </form>
    </div>
  );
}
